import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import KitchenVisualMap from '../components/KitchenVisualMap';
import { getKitchens, getPrinters, getKitchenMenuItems } from '../api/api';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

interface MenuItem {
  id: number;
  name: string;
  category: string;
}

interface Printer {
  id: number;
  name: string;
  ipAddress: string;
  isActive: boolean;
}

interface Kitchen {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
  printer?: Printer;
  menuItems: MenuItem[];
}

const VisualKitchenMapPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKitchenData();
  }, []);

  const fetchKitchenData = async () => {
    try {
      setLoading(true);
      const [kitchensData, printersData] = await Promise.all([
        getKitchens(),
        getPrinters()
      ]);

      // Create a printer map for easy lookup
      const printerMap = new Map(printersData.map((p: Printer) => [p.id, p]));

      // Fetch menu items for each kitchen and combine data
      const kitchensWithDetails = await Promise.all(
        kitchensData.map(async (kitchen: {
          id: number;
          name: string;
          type: string;
          isActive: boolean;
          printerId?: number;
        }) => {
          try {
            const menuItems = await getKitchenMenuItems(kitchen.id);
            return {
              id: kitchen.id,
              name: kitchen.name,
              type: kitchen.type,
              isActive: kitchen.isActive,
              printer: kitchen.printerId ? printerMap.get(kitchen.printerId) : undefined,
              menuItems: menuItems || []
            };
          } catch (error) {
            console.error(`Error fetching menu items for kitchen ${kitchen.id}:`, error);
            return {
              id: kitchen.id,
              name: kitchen.name,
              type: kitchen.type,
              isActive: kitchen.isActive,
              printer: kitchen.printerId ? printerMap.get(kitchen.printerId) : undefined,
              menuItems: []
            };
          }
        })
      );

      setKitchens(kitchensWithDetails);
    } catch (error) {
      console.error('Error fetching kitchen data:', error);
      toast.error('Failed to load kitchen data');
    } finally {
      setLoading(false);
    }
  };

  const handleKitchenClick = (kitchen: Kitchen) => {
    toast(`Selected: ${kitchen.name}`, {
      icon: '👨‍🍳',
      duration: 2000
    });
  };

  const handlePrinterClick = () => {
    navigate('/vendor/printers');
  };

  const handleMenuItemClick = (item: MenuItem) => {
    toast(`Selected: ${item.name}`, {
      icon: '🍽️',
      duration: 2000
    });
  };

  const handleLinkPrinter = (kitchen: Kitchen) => {
    navigate('/vendor/kitchens');
    toast('Redirecting to Kitchen Management to link printer...', {
      icon: '🔗',
      duration: 2000
    });
  };

  const handleManageItems = (kitchen: Kitchen) => {
    navigate('/vendor/kitchens');
    toast('Redirecting to Kitchen Management to manage items...', {
      icon: '📝',
      duration: 2000
    });
  };

  const handleMenuItemDrop = async (item: MenuItem, fromKitchen: Kitchen, toKitchen: Kitchen) => {
    toast(`Moving "${item.name}" from ${fromKitchen.name} to ${toKitchen.name}`, {
      icon: '🔄',
      duration: 2000
    });

    // In a real implementation, you would make an API call here to update the menu item assignment
    // For now, we'll just refresh the data
    setTimeout(() => {
      fetchKitchenData();
    }, 1000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.primary }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.background }}>
      {/* Toaster provided globally via ToastProvider */}
      
      {/* Header */}
      <div className="p-6 border-b" style={{ borderColor: theme.components?.card?.border }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/vendor/settings')}
              className="p-2 rounded-lg transition-colors"
              style={{
                backgroundColor: theme.components?.card?.background,
                color: theme.text
              }}
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold" style={{ color: theme.text }}>
              Visual Kitchen Map
            </h1>
          </div>
          <p className="text-sm" style={{ color: theme.gray?.[600] }}>
            Visualize your kitchen workflows and connections at a glance
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <KitchenVisualMap
          kitchens={kitchens}
          onKitchenClick={handleKitchenClick}
          onPrinterClick={handlePrinterClick}
          onMenuItemClick={handleMenuItemClick}
          onLinkPrinter={handleLinkPrinter}
          onManageItems={handleManageItems}
          onMenuItemDrop={handleMenuItemDrop}
        />
      </div>

      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            className="p-4 rounded-lg text-center"
            style={{ backgroundColor: theme.components?.card?.background }}
          >
            <div className="text-2xl font-bold" style={{ color: theme.primary }}>
              {kitchens.length}
            </div>
            <div className="text-sm" style={{ color: theme.gray?.[600] }}>
              Total Kitchens
            </div>
          </div>
          <div 
            className="p-4 rounded-lg text-center"
            style={{ backgroundColor: theme.components?.card?.background }}
          >
            <div className="text-2xl font-bold" style={{ color: theme.success }}>
              {kitchens.filter(k => k.isActive).length}
            </div>
            <div className="text-sm" style={{ color: theme.gray?.[600] }}>
              Active Kitchens
            </div>
          </div>
          <div 
            className="p-4 rounded-lg text-center"
            style={{ backgroundColor: theme.components?.card?.background }}
          >
            <div className="text-2xl font-bold" style={{ color: theme.warning }}>
              {kitchens.filter(k => k.printer).length}
            </div>
            <div className="text-sm" style={{ color: theme.gray?.[600] }}>
              With Printers
            </div>
          </div>
          <div 
            className="p-4 rounded-lg text-center"
            style={{ backgroundColor: theme.components?.card?.background }}
          >
            <div className="text-2xl font-bold" style={{ color: theme.accent || theme.primary }}>
              {kitchens.reduce((sum, k) => sum + k.menuItems.length, 0)}
            </div>
            <div className="text-sm" style={{ color: theme.gray?.[600] }}>
              Total Assignments
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualKitchenMapPage;